import { useMemo, useCallback, ReactNode } from 'react';
import { ProcessingStrategy_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/auto-rules/v1/models.pb';
import { DataType } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/capability.pb';
import { IntegrationsService } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integrations_service.pb.js';
import { AutoRulesQueries } from '@frontend/api-auto-rules';
import { type PersonTypes, PersonTypes as PersonTypesEnums } from '@frontend/api-person';
import { AutoMessageDetailContactPanel } from '@frontend/auto-message-detail-contact-panel';
import { PatientInsights, type PatientInsightsActions } from '@frontend/call-intel-insights';
import { useDeleteContactAction } from '@frontend/contact-actions-delete-contact';
import { useContactHistory } from '@frontend/contact-history-panel';
import { useDemoPanelShallowStore, useIsInsideDemoPanel } from '@frontend/demo-panels';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { useMessagingFeaturesEnabled } from '@frontend/messaging-enabled-hook';
import { WIDTH_DIMS } from '@frontend/panel-engine';
import { bindHTTP } from '@frontend/schema';
import { useScopedAppFlagStore, useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { ContactPanelTabsType } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';
import { ContactDetails } from '../contact-details/contact-details';
import { DemoContactPanelComponent } from '../demo-contact-panel/demo-contact-panel-component';
import { AppointmentSectionContainer } from './sections/appointment-section/appointment-section-container';
import { ContactBalance } from './sections/contact-balance';
import { CustomerInformation } from './sections/customer-information';
import { Household } from './sections/household';
import { PetOverview } from './sections/pet-overview';

export const SchemaAddressService = bindHTTP(IntegrationsService);
export type CustomerDetailsProps = {
  data?: PersonTypes.Person;
  locationId?: string;
  onTabChange?: (tab: ContactPanelTabsType) => void;
};

export const CustomerDetails = ({ data, locationId, onTabChange }: CustomerDetailsProps) => {
  const sourceId = data?.SourceID;
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();

  const { data: integrationsQueryData } = useScopedQuery({
    queryKey: ['integrations', sourceId],
    queryFn: () => SchemaAddressService.GetDataSourceBySourceID({ sourceId }),
    enabled: !!sourceId,
  });

  const hasAgingIntegration = integrationsQueryData?.dataSource?.integration?.capabilities?.find(
    (capability) => capability.dataType === DataType.AGING
  );

  const hasRecallIntegration = integrationsQueryData?.dataSource?.integration?.capabilities?.find(
    (capability) => capability.dataType === DataType.RECALL
  );

  const isCustomContact = data?.SourceType === PersonTypesEnums.PersonSourceType['CustomContact'];
  const isCsv = data?.SourceType === PersonTypesEnums.PersonSourceType['Csv'];
  const { lastUsedVertical } = useLastUsedVerticalShallowStore('lastUsedVertical');

  const isVet = lastUsedVertical === 'VET';

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const nonIntegratedContactPanelFeatureFlag = getFeatureFlagValue('cc-appointmentstatus');

  const { replaceFilter } = useContactHistory();

  const contactIsIntegrated = !isCustomContact && !isCsv;
  const showAppointmentStatus = contactIsIntegrated || nonIntegratedContactPanelFeatureFlag;

  const { data: messageTypes } = AutoRulesQueries.useListAutoMessageTypes({
    orgId: selectedOrgId,
    businessGroupIds: selectedLocationIds,
  });
  const messagingEnabled = useMessagingFeaturesEnabled();
  const showAutoMessageDetails =
    messagingEnabled &&
    Object.values(messageTypes || {}).filter(
      (messageType) =>
        messageType.processingStrategy !== ProcessingStrategy_Enum.ON_SYNC &&
        messageType.processingStrategy !== ProcessingStrategy_Enum.INVALID
    ).length > 0;

  const patientInsightsActions = useMemo<PatientInsightsActions>(
    () => ({
      onChartClick: () => onTabChange?.('history-tab'),
      onFilterChange: (filter) => replaceFilter({ scope: 'patientInsight', ...filter }),
      onPrimaryActionClick: () => onTabChange?.('history-tab'),
    }),
    [onTabChange, replaceFilter]
  );

  const { register: registerInDemoPanel, setHeaderConfig: setDemoPanelHeaderConfig } = useDemoPanelShallowStore(
    'register',
    'setHeaderConfig'
  );

  const isDemoPanelView = useIsInsideDemoPanel();

  const handleViewDemo = useCallback(
    ({ action }: { action?: ReactNode }) => {
      if (!locationId) {
        return;
      }
      setDemoPanelHeaderConfig({
        action: { render: action },
        iconName: 'premium-badge',
      });
      registerInDemoPanel({
        id: 'demo-contact-panel',
        component: DemoContactPanelComponent,
        componentProps: {
          demoLocationId: locationId,
          demoFeatures: ['patient-insights', 'call-intel-insights'],
        },
        width: WIDTH_DIMS['medium'],
      });
    },
    [registerInDemoPanel, setDemoPanelHeaderConfig, locationId]
  );

  return (
    <>
      {isVet ? <PetOverview data={data} locationId={locationId} /> : <Household data={data} />}
      <ContactDetails>
        <CustomerInformation data={data} locationId={locationId} />
      </ContactDetails>
      <AppointmentSectionContainer
        data={data}
        locationId={locationId}
        showAppointmentStatus={showAppointmentStatus}
        hasRecallIntegration={!!hasRecallIntegration}
      />
      <PatientInsights
        person={data}
        actions={patientInsightsActions}
        locationId={locationId}
        wrapper={ContactDetails}
        isDemoView={isDemoPanelView}
        onViewDemo={handleViewDemo}
      />
      {contactIsIntegrated && hasAgingIntegration && (
        <ContactDetails>
          <ContactBalance data={data} />
        </ContactDetails>
      )}
      {showAutoMessageDetails && <AutoMessageDetailContactPanel person={data} />}
      {isCustomContact && <DeleteContact personId={data.PersonID} locationId={locationId || ''} />}
    </>
  );
};

const DeleteContact = ({ personId, locationId }: { personId: string; locationId: string }) => {
  const tokens = useTranslationTokens();
  const { triggerProps, DeleteConfirmationModal } = useDeleteContactAction({ personId, locationId });

  return (
    <>
      <div css={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant='tertiary'
          trackingId='contact-panel-delete-contact-btn'
          color='critical'
          destructive
          iconName='trash'
          size='large'
          css={{ color: theme.colors.status.critical }}
          {...triggerProps}
        >
          {tokens.deleteContact}
        </Button>
      </div>
      {DeleteConfirmationModal}
    </>
  );
};
